import { Component, ChangeDetectionStrategy, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/common/user_and_auth/auth.service';
import { UserService } from 'src/app/common/user_and_auth/user.service';
import { OAuthService } from 'src/app/common/user_and_auth/oauth.service';
import { OAuthProviderConfig, OAuthProviderId, OAUTH_PROVIDERS, availableProviders } from 'src/app/common/user_and_auth/oauth-providers';

/** If the page hasn't unloaded after this long, treat the OAuth redirect as failed. */
const OAUTH_REDIRECT_TIMEOUT_MS = 10_000;

@Component({
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, OnDestroy {

  formUserEmail = '';
  formUserPassword = '';
  formConfirmPassword = '';
  formDisplayName = '';
  readonly message = signal('');
  readonly statusMessage = signal('');
  readonly loading = signal(false);
  readonly isSignupMode = signal(false);
  readonly oauthProviders: OAuthProviderConfig[] = availableProviders();

  private auth = inject(AuthService);
  private user = inject(UserService);
  private oauth = inject(OAuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private redirectTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    if (this.user.user()) {
      this.router.navigateByUrl(this.safeReturnUrl());
    }
  }

  ngOnDestroy(): void {
    if (this.redirectTimer) clearTimeout(this.redirectTimer);
  }

  async login(): Promise<void> {
    this.message.set('');
    this.statusMessage.set('');
    this.loading.set(true);
    try {
      await this.auth.loginUsingPassword(this.formUserEmail, this.formUserPassword);
      this.router.navigateByUrl(this.safeReturnUrl());
    } catch (e: any) {
      this.message.set(e?.message ?? String(e));
    } finally {
      this.loading.set(false);
    }
  }

  async signup(): Promise<void> {
    if (!this.canSignup()) return;
    this.message.set('');
    this.statusMessage.set('');
    this.loading.set(true);
    try {
      await this.auth.signupUsingPassword(this.formUserEmail, this.formUserPassword, this.formDisplayName);
      this.router.navigateByUrl(this.safeReturnUrl());
    } catch (e: any) {
      this.message.set(e?.message ?? String(e));
    } finally {
      this.loading.set(false);
    }
  }

  canSignup(): boolean {
    return !!this.formUserEmail
      && !!this.formUserPassword
      && this.formUserPassword.length >= 6
      && this.formUserPassword === this.formConfirmPassword
      && !this.loading();
  }

  toggleMode(): void {
    this.isSignupMode.update(v => !v);
    this.message.set('');
    this.statusMessage.set('');
  }

  /**
   * Start an OAuth login. On success the browser navigates to the provider and this
   * page unloads. A timeout guards against the rare case where navigation never
   * happens (popup blocker, abort, etc.) so the spinner doesn't stick forever.
   */
  async loginWith(providerId: OAuthProviderId): Promise<void> {
    this.message.set('');
    this.statusMessage.set(`Redirecting to ${OAUTH_PROVIDERS[providerId].displayName}…`);
    this.loading.set(true);
    this.redirectTimer = setTimeout(() => {
      this.redirectTimer = null;
      this.statusMessage.set('');
      this.message.set('Redirect to sign-in provider failed. Please try again.');
      this.loading.set(false);
    }, OAUTH_REDIRECT_TIMEOUT_MS);

    try {
      await this.oauth.startLogin(providerId, this.safeReturnUrl());
    } catch (e: any) {
      if (this.redirectTimer) {
        clearTimeout(this.redirectTimer);
        this.redirectTimer = null;
      }
      this.statusMessage.set('');
      this.message.set(e?.message ?? String(e));
      this.loading.set(false);
    }
  }

  /** Only allow same-origin paths to prevent open-redirect via ?returnUrl=. */
  private safeReturnUrl(): string {
    const raw = this.route.snapshot.queryParamMap.get('returnUrl') ?? '/';
    if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
    if (raw.startsWith('/login')) return '/';
    return raw;
  }
}
